import { api } from "@/lib/api";
import {
  ServiceProviderDomain,
  ServiceProviderDTO,
  serviceProviderDtoListSchema,
  ServiceProviderDTOSchema,
  serviceProviderFromDto,
} from "@/models";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";

const toastDomainErrorMessageMap: Record<ServiceProviderDomain, string> = {
  service: "toast.getServiceProviderProviderError",
  provider: "toast.getServiceProviderServiceError",
};
const toastDomainGetOneErrorMessageMap: Record<ServiceProviderDomain, string> =
  {
    service: "toast.getServiceProviderProviderDetailError",
    provider: "toast.getServiceProviderServiceDetailError",
  };

export function getServiceProvidersQueryOptions(
  domain: ServiceProviderDomain,
  domainId: string,
  filters?: Record<string, unknown> | undefined,
) {
  return queryOptions({
    queryKey: ["service-providers", domain, domainId],
    queryFn: async () => {
      const result: Array<ServiceProviderDTO> = [];
      let page = 0;
      let hasNextPage = true;
      while (hasNextPage) {
        page += 1;
        const res = await api.getWithCache(
          "/api/service-providers",
          serviceProviderDtoListSchema,
          {
            query: {
              page: page.toString(),
            },
            filters: filters,
            projection: ["Provider.Person", "Service", "Region"],
          },
        );
        result.push(...res.Data);
        hasNextPage = res.PageInfo.HasNextPage;
      }
      return result.map(serviceProviderFromDto);
    },
  });
}

export function useGetServiceProviders<TERROR extends Error>(
  domain: ServiceProviderDomain,
  domainId: string,
  opts?: {
    onError?: (error: TERROR) => void;
    enabled?: boolean;
  },
) {
  const { toast } = useToast();

  const q = useQuery({
    ...getServiceProvidersQueryOptions(domain, domainId, {
      ServiceId: domain === "service" ? Number.parseInt(domainId) : undefined,
      ProviderId: domain === "provider" ? Number.parseInt(domainId) : undefined,
    }),
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: toastDomainErrorMessageMap[domain],
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [domain, opts, q.error, toast]);
  return q;
}

export const getAllServiceProvidersQueryOptions = queryOptions({
  ...getServiceProvidersQueryOptions(
    undefined as unknown as ServiceProviderDomain,
    undefined as unknown as string,
    undefined,
  ),
  queryKey: ["all-service-providers"],
});

export function useGetAllServiceProviders(opts?: { enabled?: boolean }) {
  return useQuery({
    ...getAllServiceProvidersQueryOptions,
    enabled: opts?.enabled,
  });
}

export function getServiceProviderQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["service-providers-detail", id],
    queryFn: () =>
      api
        .get("/api/service-providers/$id", ServiceProviderDTOSchema, {
          pathReplace: { $id: id },
          projection: ["Provider.Person", "Service", "Region"],
        })
        .then(serviceProviderFromDto),
  });
}

export function useGetServiceProvider<TERROR extends Error>(
  domain: ServiceProviderDomain,
  id: string,
  opts?: { onError?: (error: TERROR) => void; enabled?: boolean },
) {
  const { toast } = useToast();
  const q = useQuery({
    ...getServiceProviderQueryOptions(id),
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: toastDomainGetOneErrorMessageMap[domain],
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [domain, opts, q.error, toast]);
  return q;
}
